'use client';

// Browser-side Polygon RPC + tx broadcasting via viem.
//
// We talk to a public Polygon RPC directly from the browser — these calls
// are read-only or carry an already-signed transaction, so they don't
// reveal anything sensitive about the user. The vault's pk is only
// touched inside this module's signApprovalTx() and never returned.

import {
  createPublicClient,
  createWalletClient,
  encodeFunctionData,
  http,
  type Account,
  type Address,
  type Hex,
  type PublicClient,
  type TransactionReceipt,
} from 'viem';
import { polygon } from 'viem/chains';
import {
  CTF_ADDRESS,
  CTF_EXCHANGE_ADDRESS,
  ERC1155_ABI,
  ERC20_ABI,
  MAX_UINT256,
  POLYGON_CHAIN_ID,
  USDC_ADDRESS,
} from './contracts.js';

// Default public RPC — polygon-rpc.com allows CORS for browser JSON-RPC.
// Operators can override via NEXT_PUBLIC_POLYGON_RPC_URL at build time.
const DEFAULT_RPC =
  (typeof process !== 'undefined' &&
    process.env?.['NEXT_PUBLIC_POLYGON_RPC_URL']) ||
  'https://polygon-rpc.com';

function publicClient(): PublicClient {
  return createPublicClient({ chain: polygon, transport: http(DEFAULT_RPC) }) as PublicClient;
}

export interface ApprovalState {
  usdcAllowance: bigint;
  ctfApproved: boolean;
}

export async function readApprovalState(address: Address): Promise<ApprovalState> {
  const client = publicClient();
  const [usdcAllowance, ctfApproved] = await Promise.all([
    client.readContract({
      address: USDC_ADDRESS,
      abi: ERC20_ABI,
      functionName: 'allowance',
      args: [address, CTF_EXCHANGE_ADDRESS],
    }) as Promise<bigint>,
    client.readContract({
      address: CTF_ADDRESS,
      abi: ERC1155_ABI,
      functionName: 'isApprovedForAll',
      args: [address, CTF_EXCHANGE_ADDRESS],
    }) as Promise<boolean>,
  ]);
  return { usdcAllowance, ctfApproved };
}

export interface ApprovalProgress {
  step: 'usdc' | 'ctf';
  txHash: Hex;
}

// Signs + broadcasts a USDC.approve(CTFExchange, MAX_UINT256) tx using a
// caller-supplied viem Account (built inside the vault module so the pk
// never leaks here). Returns the tx hash on success.
export async function approveUsdc(account: Account): Promise<Hex> {
  const wallet = createWalletClient({ account, chain: polygon, transport: http(DEFAULT_RPC) });
  const data = encodeFunctionData({
    abi: ERC20_ABI,
    functionName: 'approve',
    args: [CTF_EXCHANGE_ADDRESS, MAX_UINT256],
  });
  return wallet.sendTransaction({
    to: USDC_ADDRESS,
    data,
    value: 0n,
    chain: polygon,
  });
}

export async function approveCtf(account: Account): Promise<Hex> {
  const wallet = createWalletClient({ account, chain: polygon, transport: http(DEFAULT_RPC) });
  const data = encodeFunctionData({
    abi: ERC1155_ABI,
    functionName: 'setApprovalForAll',
    args: [CTF_EXCHANGE_ADDRESS, true],
  });
  return wallet.sendTransaction({
    to: CTF_ADDRESS,
    data,
    value: 0n,
    chain: polygon,
  });
}

// Polls the receipt every ~3s until the tx is confirmed. Throws if the
// receipt comes back reverted.
export async function waitForReceipt(hash: Hex, timeoutMs = 120_000): Promise<TransactionReceipt> {
  const client = publicClient();
  const receipt = await client.waitForTransactionReceipt({ hash, timeout: timeoutMs });
  if (receipt.status !== 'success') {
    throw new Error(`tx ${hash} reverted`);
  }
  return receipt;
}

// Quick read of the USDC balance (in 6-decimal cents per Polymarket).
export async function readUsdcBalance(address: Address): Promise<bigint> {
  // ABI doesn't include balanceOf in the minimal export — add inline.
  const client = publicClient();
  const balance = (await client.readContract({
    address: USDC_ADDRESS,
    abi: [
      {
        name: 'balanceOf',
        type: 'function',
        stateMutability: 'view',
        inputs: [{ name: 'owner', type: 'address' }],
        outputs: [{ type: 'uint256' }],
      },
    ] as const,
    functionName: 'balanceOf',
    args: [address],
  })) as bigint;
  return balance;
}

export function formatUsdc(amount: bigint): string {
  // 6 decimals
  const whole = amount / 1_000_000n;
  const frac = amount % 1_000_000n;
  return `${whole}.${frac.toString().padStart(6, '0').slice(0, 2)}`;
}

export { POLYGON_CHAIN_ID };
